import { useState } from "react";
import { Disc3, ListMusic, Music, type LucideIcon } from "lucide-react";
import { Button, DialogCancel, Field, Icon } from "@cntrl/ui";
import { useCreateRelease } from "../../app/hooks/useReleases.js";
import { releaseGradient, type ReleaseType } from "./releaseFormat.js";
import { Modal } from "./Modal.js";
import styles from "./NewReleaseModal.module.scss";

// The new-release TYPE PICKER (shot-47) — "What are you putting out?": Single / EP / Album gradient
// cards. The created release lands in DRAFTS (the server births it `draft`), stamped with the band's to-do
// template, then the modal deep-links into it. Create is server-confirm THEN navigate (never a phantom
// success).
//
// There is deliberately NO checklist picker here any more. A band has ONE template and every release gets
// it, so a chooser would be a control with a single option; it is edited in Release settings instead.
//
// The heading comes from the `Modal` primitive, not a local `<h2>` behind `hideHeader`: dialog titles are
// display-face UPPERCASE 16px and that treatment lives in the primitive (ruling 3), so drawing our own
// bypassed it. The primitive also owns the close gutter, which is the export's own `.rl-modal__title`
// defect ("a long title runs under the ✕") fixed by construction.

const TYPE_CARDS: { type: ReleaseType; title: string; blurb: string; icon: LucideIcon }[] = [
  { type: "single", title: "Single", blurb: "One track. The fastest rollout.", icon: Music },
  { type: "ep", title: "EP", blurb: "3-6 tracks under one cover.", icon: ListMusic },
  { type: "album", title: "Album", blurb: "A full body of work.", icon: Disc3 },
];

export interface NewReleaseModalProps {
  onClose: () => void;
  onCreated: (releaseId: string) => void;
}

export function NewReleaseModal({ onClose, onCreated }: NewReleaseModalProps) {
  const create = useCreateRelease();
  const [selected, setSelected] = useState<ReleaseType | null>(null);
  const [name, setName] = useState("");
  const [error, setError] = useState<string | null>(null);

  function submit() {
    if (!selected) return;
    setError(null);
    create.mutate(
      {
        title: name.trim() || "Untitled release",
        releaseType: selected,
      },
      {
        onSuccess: (result) => {
          const id = result.data?.overview?.releaseId;
          if (id) onCreated(id);
          else onClose();
        },
        onError: (err) => setError(err.message),
      },
    );
  }

  return (
    <Modal
      title="What are you putting out?"
      onClose={onClose}
      size="lg"
    >
      <div className={styles.wrap}>
        <div className={styles.cards} role="radiogroup" aria-label="Release type">
          {TYPE_CARDS.map((c) => (
            <button
              key={c.type}
              type="button"
              role="radio"
              aria-checked={selected === c.type}
              className={styles.card}
              data-selected={selected === c.type || undefined}
              onClick={() => setSelected(c.type)}
            >
              <span className={styles.cardArt} style={{ background: releaseGradient(c.type) }} aria-hidden="true">
                <Icon icon={c.icon} size={32} className={styles.cardIcon} />
              </span>
              <span className={styles.cardTitle}>{c.title}</span>
              <span className={styles.cardBlurb}>{c.blurb}</span>
            </button>
          ))}
        </div>

        {/* The label follows the picked type ("EP name"); blank falls back to "Untitled release". */}
        <Field
          label={`${TYPE_CARDS.find((c) => c.type === selected)?.title ?? "Release"} name`}
          value={name}
          maxLength={200}
          onChange={(e) => setName(e.target.value)}
        />

        {error && (
          <p className={styles.error} role="alert">
            {error}
          </p>
        )}

        <div className={styles.actions}>
          <DialogCancel disabled={create.isPending} />
          <Button onClick={submit} disabled={!selected} loading={create.isPending}>
            {selected ? `Create ${TYPE_CARDS.find((c) => c.type === selected)?.title}` : "Choose a type"}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
